import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
} = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function TurnOneTextIntoTenPosts() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="9028" id="blockchain-recovery-experts" className="relative min-h-[90vh] w-full flex flex-col items-center justify-center overflow-hidden bg-background text-white dark py-20 px-4">
      {}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">

        {}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[100px] transition-transform duration-100 ease-out will-change-transform" />
        
        {}
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-accent/20 rounded-full blur-[120px] transition-transform duration-100 ease-out will-change-transform" />

        {}
        <div className="absolute top-10 right-10 w-64 h-64 bg-primary/5 rounded-full blur-[80px] transition-transform duration-100 ease-out will-change-transform" />

        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#18ffff08_1px,transparent_1px),linear-gradient(to_bottom,#18ffff08_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
      </div>

      {}
      <div ref={textRef} className={`relative z-10 flex flex-col items-center text-center max-w-5xl mx-auto space-y-8 transition-all duration-1000 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        {}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-white text-xs font-mono font-medium mb-4 animate-in fade-in zoom-in duration-700 delay-100 uppercase tracking-widest">
          <Sparkles className="w-4 h-4" />
          <span>Certified Forensic Analysis</span>
        </div>

        {}
        <h1 className="text-5xl md:text-7xl lg:text-[72px] leading-[1.1] font-bold tracking-tighter font-sans">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-primary">
            Recover Your Lost
          </span>
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary to-white">
            Cryptocurrency
          </span>
        </h1>

        {}
        <p className="text-lg md:text-xl text-white max-w-2xl mx-auto leading-relaxed font-mono">
          No Upfront Fees for Evaluation. Our specialists utilize advanced node-tracing technology to locate and secure missing digital assets with clinical precision.
        </p>

        {}
        <div className="pt-8">
          <Link to="/ContactPage">
            <Button size="lg" className="relative h-14 px-10 text-lg bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm shadow-[0_0_30px_rgba(0,255,255,0.3)] hover:shadow-[0_0_50px_rgba(0,255,255,0.5)] transition-all duration-300 group overflow-hidden border border-primary/50">
              <span className="relative z-10 flex items-center gap-2 font-sans font-bold uppercase tracking-wide">
                Start Your Recovery
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
              
              {}
              <div className="absolute inset-0 rounded-sm bg-white/10 animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite] opacity-0 group-hover:opacity-100" />
              
              {}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] z-0" />
            </Button>
          </Link>
        </div>

        {}
        <div className="pt-12 flex flex-col items-center gap-4 opacity-40">
          <p className="text-xs text-white uppercase tracking-[0.3em] font-mono font-medium">
            Global Forensic Partners
          </p>
          <div className="flex items-center gap-8 grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-500">
             {}
             <span className="text-lg font-bold font-sans text-white tracking-tighter">COINDESK</span>
             <span className="text-lg font-bold font-sans text-white tracking-tighter">FORBES</span>
             <span className="text-lg font-bold font-sans text-white tracking-tighter">WIRED</span>
          </div>
        </div>
      </div>
    </section>;
}